import React, { useState, useEffect, useMemo } from "react";
import { List as ListEntity } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, Plus, List as ListIcon, MoreVertical, ArrowUp, ArrowDown, FolderMinus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useData } from "../components/DataContext";
import AuthLoader from "../components/auth/AuthLoader";
import SignInRequired from "../components/auth/SignInRequired";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Folder, FolderColor } from "@/types";

const colorVariants: Record<FolderColor, { bg: string; text: string; border: string }> = {
  blue: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  green: { bg: "bg-green-100", text: "text-green-800", border: "border-green-300" },
  purple: { bg: "bg-purple-100", text: "text-purple-800", border: "border-purple-300" },
  orange: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300" },
  red: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
  teal: { bg: "bg-teal-100", text: "text-teal-800", border: "border-teal-300" },
};

export default function ViewFolder() {
  const navigate = useNavigate();
  const { folders, lists, sections, listItems, isLoading, runOptimisticUpdate, user, userLoading } = useData();
  const [folder, setFolder] = useState<Folder | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const folderId = urlParams.get('id');

  useEffect(() => {
    if (!isLoading) {
      const currentFolder = folders.find(f => f.id === folderId) || null;
      setFolder(currentFolder);
    }
  }, [folderId, folders, isLoading]);
  
  const folderLists = useMemo(() => {
    if (!folder) return [];
    return lists
      .filter(l => l.folder_id === folderId)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  }, [folder, lists, folderId]);

  const handleMove = async (id: string, direction: 'up' | 'down') => {
    const items = folderLists;
    const index = items.findIndex(item => item.id === id);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const originalState = { lists: [...lists] };
    const itemA = items[index];
    const itemB = items[newIndex];
    
    const task = async () => {
      await Promise.all([
        ListEntity.update(itemA.id, { position: newIndex }),
        ListEntity.update(itemB.id, { position: index })
      ]);
    };
    
    const newState = {
      folders: [...folders],
      lists: lists.map(list => {
        if (list.id === itemA.id) return { ...list, position: newIndex };
        if (list.id === itemB.id) return { ...list, position: index };
        return list;
      }),
      sections: [...sections],
      listItems: [...listItems]
    };

    runOptimisticUpdate(task, { update: newState, originalState: { folders, ...originalState, sections, listItems } });
  };

  const handleRemoveFromFolder = async (listId: string) => {
    const originalState = { lists: [...lists] };
    const task = async () => ListEntity.update(listId, { folder_id: null });
    const newState = {
      folders: [...folders],
      lists: lists.map(list => 
        list.id === listId ? { ...list, folder_id: null } : list
      ),
      sections: [...sections],
      listItems: [...listItems]
    };
    runOptimisticUpdate(task, { update: newState, originalState: { folders, ...originalState, sections, listItems } });
  };

  if (userLoading) {
    return <AuthLoader />;
  }

  if (!user) {
    return <SignInRequired />;
  }

  if (isLoading && !folder) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  if (!folder) {
    return <div className="p-8 text-center">Folder not found.</div>;
  }
  
  const color = colorVariants[folder.color] || colorVariants.blue;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(createPageUrl("Dashboard"))}
              className="border-slate-200 hover:bg-slate-50"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className={`text-3xl md:text-4xl font-bold ${color.text}`}>
                {folder.name}
              </h1>
              <p className="text-slate-600">Manage the lists in this folder.</p>
            </div>
          </div>
          <Link to={createPageUrl(`CreateList?folder_id=${folder.id}`)} className="w-full md:w-auto">
            <Button className="w-full bg-blue-600 hover:bg-blue-700 shadow-lg">
              <Plus className="w-4 h-4 mr-2" />
              New List in Folder
            </Button>
          </Link>
        </div>

        {/* Lists */}
        {folderLists.length > 0 ? (
          <div className="space-y-2">
            <AnimatePresence>
              {folderLists.map((list, index) => (
                <motion.div
                  key={list.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 group"
                >
                  <Link 
                    to={createPageUrl(`EditList?id=${list.id}`)} 
                    className="flex items-center gap-4 p-4"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ListIcon className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate">{list.title}</h3>
                      <p className="text-sm text-slate-500">Click to edit</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="opacity-0 group-hover:opacity-100 flex-shrink-0"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.preventDefault();
                            handleMove(list.id, 'up');
                          }} 
                          disabled={index === 0}
                        >
                          <ArrowUp className="w-4 h-4 mr-2" />
                          Move Up
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.preventDefault();
                            handleMove(list.id, 'down');
                          }} 
                          disabled={index === folderLists.length - 1}
                        >
                          <ArrowDown className="w-4 h-4 mr-2" />
                          Move Down
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.preventDefault();
                            handleRemoveFromFolder(list.id);
                          }}
                        >
                          <FolderMinus className="w-4 h-4 mr-2" />
                          Remove from Folder
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No lists in this folder yet</h3>
            <p className="text-slate-600 mb-6">Create your first list to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
